/**
 * Napoli — tier display helpers.
 *
 * Backend stores tier slugs in lowercase (`ospite`, `amico`, `famiglia`,
 * `padrone`). UI everywhere should render them title-cased — never UPPERCASE
 * and never raw lowercase. One helper, used everywhere, keeps it consistent.
 */
export const TIER_ORDER = ["ospite", "amico", "famiglia", "padrone"];

export const TIER_LABELS = {
  ospite: "Ospite",
  amico: "Amico",
  famiglia: "Famiglia",
  padrone: "Padrone",
};

export const TIER_TAGLINES = {
  ospite: "The first welcome.",
  amico: "Now we know you.",
  famiglia: "You're at the family table.",
  padrone: "The house is yours.",
};

export const TIER_FALLBACK_PERKS = {
  ospite: [
    "Welcome bonus on signup",
    "Birthday treat from the kitchen",
  ],
  amico: [
    "Earn 1.25× points on every visit",
    "Priority seating on weeknights",
  ],
  famiglia: [
    "Earn 1.5× points on every visit",
    "Complimentary aperitivo on arrival",
    "Reserved table any night you call",
  ],
  padrone: [
    "Earn 2× points on every visit",
    "Chef's tasting menu twice a year",
    "Private dining room access, first refusal",
  ],
};

export const tierLabel = (slug) => TIER_LABELS[slug] || "Ospite";
export const tierTagline = (slug) => TIER_TAGLINES[slug] || "";
export const tierFallbackPerks = (slug) => TIER_FALLBACK_PERKS[slug] || [];

/** Stable, day-locked greeting string. Same all day, changes overnight. */
export const dayGreeting = (firstName, now = new Date()) => {
  const h = now.getHours();
  const part = h < 11 ? "Buongiorno" : h < 17 ? "Buon pomeriggio" : "Buona sera";
  return `${part}, ${firstName || "amico"}.`;
};

/**
 * Today-at-Napoli — DETERMINISTIC by weekday. Same message all day,
 * changes at midnight. NOT random. Day index 0 = Sunday.
 */
const TODAY_MESSAGES = [
  {
    eyebrow: "Domenica",
    body: "Lazy Sunday lunch. The carafe of house red is on us with any pasta.",
  },
  {
    eyebrow: "Lunedì",
    body: "Quiet Monday. Chef's special pasta is half points until 7pm.",
  },
  {
    eyebrow: "Martedì",
    body: "Tuesday tasting. The burrata flight just landed on the bar menu.",
  },
  {
    eyebrow: "Mercoledì",
    body: "Wine Wednesday. Every glass on the list earns double points.",
  },
  {
    eyebrow: "Giovedì",
    body: "Thursday Aperitivo. Negroni flights, 5 to 7, the back room is open.",
  },
  {
    eyebrow: "Venerdì",
    body: "Weekend opens here. Live jazz from 8 — no cover for members.",
  },
  {
    eyebrow: "Sabato",
    body: "Saturday late seating still has room. Call before 6 to lock it in.",
  },
];

export const todayAtNapoli = (now = new Date()) =>
  TODAY_MESSAGES[now.getDay()];

/** Friendly relative time, US-style. Used for transaction timeline. */
export const relativeTime = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Date.now() - then;
  const m = Math.floor(diff / 60000);
  if (m < 1) return "Just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d} day${d === 1 ? "" : "s"} ago`;
  const w = Math.floor(d / 7);
  if (w < 5) return `${w} week${w === 1 ? "" : "s"} ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo} month${mo === 1 ? "" : "s"} ago`;
  return `${Math.floor(d / 365)} yr ago`;
};

/** Tier index 0..3. Useful for grouping in <Rewards /> page. */
export const tierIndex = (slug) => Math.max(0, TIER_ORDER.indexOf(slug));
